<template lang="pug">
  DefaultLayout
    CommonSection(v-if="referral" :title="`${referral.name} ${referral.surname}`" :back="{ title: 'Рефералы', name: 'affiliate-referrals' }")
      .b-affiliate-referral-detail-page
        ._tabs
          AffiliateTabs(tab="affiliate-referrals")
        FormBlock._main
          BaseTable._table(:headers="$options.headers" :items="payments" v-slot="{ items, headers, sort }")
            ._row(v-for="item in items" :key="item.id")
              ._cell.-bold {{ item.externalId }}
                BaseTableHeaderItem._header(v-bind="headers[0]" @sort="sort")
              ._cell {{ formatDate(item.createdAt) }}
                BaseTableHeaderItem._header(v-bind="headers[1]" @sort="sort")
              ._cell 30%
                BaseTableHeaderItem._header(v-bind="headers[2]" @sort="sort")
              ._cell {{ item.amount / 100 }} ₽
                BaseTableHeaderItem._header(v-bind="headers[3]" @sort="sort")
              ._cell.-bold {{ item.amount / 500 }} ₽
                BaseTableHeaderItem._header(v-bind="headers[4]" @sort="sort")
        FormBlock._aside
          ._aside-header Профиль реферала
          UserTile(:user="referral")
          UiKeyVal._aside-referral-info(:items="referralInfo")
          ._aside-subtitle Общая прибыль:
          ._aside-value {{ Math.round(referral.paymentsSum * 0.3) / 100 }} ₽
        FormBlock(mobile)
          ChartBar(v-bind="paymentsStats")
</template>

<script>
import affiliateModel from '@/model/affiliate';
import utils from '@/utils/common';

import DefaultLayout from '@/layouts/Default.vue';
import CommonSection from '@/components/CommonSection.vue';
import BaseTable from '@/components/base/BaseTable.vue';
import BaseTableHeaderItem from '@/components/base/BaseTableHeaderItem.vue';
import FormBlock from '@/components/FormBlock.vue';
import UserTile from '@/components/UserTile.vue';
import UiKeyVal from '@/components/ui/UiKeyVal.vue';
import AffiliateTabs from '@/components/affiliate/AffiliateTabs.vue';
import ChartBar from '@/components/chart/ChartBar.vue';

export default {
  name: 'PersonalAffiliateReferralsPage',
  components: {
    UserTile,
    FormBlock,
    DefaultLayout,
    CommonSection,
    BaseTable,
    BaseTableHeaderItem,
    UiKeyVal,
    AffiliateTabs,
    ChartBar,
  },
  headers: [
    { title: 'Код платежа' },
    { title: 'Дата', sortFn: (a, b) => a.createdAt - b.createdAt },
    { title: 'Процент' },
    { title: 'Платежи', sortFn: (a, b) => a.amount - b.amount },
    { title: 'Прибыль', sortFn: (a, b) => a.amount - b.amount },
  ],
  created() {
    this.$store.dispatch('affiliate/fetch');
  },
  computed: {
    referral() {
      return this.$store.getters['affiliate/referrals']
        .find(r => r.id === +this.$route.params.referralId);
    },
    payments() {
      return (this.referral.payments ?? [])
        .filter(p => p.status === 'confirmed')
        .map(p => ({ ...p, createdAt: new Date(p.createdAt) }));
    },
    referralInfo() {
      return [
        { key: 'Дата регистрации',  val: utils.convertDateToString(this.referral.createdAt, 'short') }, // eslint-disable-line
        { key: 'Процент',           val: '30%' }, // eslint-disable-line
        { key: 'Сумма платежей',    val: `${this.referral.paymentsSum / 100} ₽` }, // eslint-disable-line
      ];
    },
    paymentsStats() { return affiliateModel.buildStats(this.payments ?? []); },
  },
  methods: {
    formatDate(date) {
      return utils.convertDateToString(date);
    },
  },
};
</script>

<style scoped lang="scss">
  .affiliate-referral-detail-page {
    display: grid;
    grid-template-columns: 1fr 312px;
    gap: 35px;
    align-items: start;

    &__tabs {
      grid-column: 1 / 2 span;
    }

    &__row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;
    }

    &__cell {
      &--bold {
        font-weight: 500;
      }
    }

    ::v-deep .cell, &__cell {
      text-align: center;
      justify-content: center;

      &:nth-child(1) {
        text-align: left;
        justify-content: flex-start;
      }

      &:nth-child(1) { width: 40%; }
      &:nth-child(2) { width: 15%; }
      &:nth-child(3) { width: 11%; }
      &:nth-child(4) { width: 16%; }
      &:nth-child(5) { width: 18%; }
    }

    &__header {
      display: none;
    }

    &__aside {
      grid-row: 2 / 2 span;
      grid-column: 2;

      &-header {
        font-weight: 500;
        font-size: 18px;
        line-height: 24px;
        color: #1E3147;
        border-bottom: 1px solid #EBEFF2;
        margin-bottom: 20px;
        padding-bottom: 12px;
      }

      &-referral-info {
        margin: 20px 0;
      }

      &-value {
        font-weight: 700;
        font-size: 30px;
        line-height: 38px;
        letter-spacing: 0.01em;
        color: #33A1FB;
      }

      &-subtitle {
        font-weight: 500;
        font-size: 14px;
        line-height: 18px;
        letter-spacing: 0.01em;
        color: #92A0B0;
        padding-top: 20px;
        border-top: 1px solid #EBEFF2;
      }
    }

    @media (max-width: 1300px) {
      grid-template-columns: 1fr;

      &__tabs {
        grid-column: 1 / 1 span;
      }

      &__aside {
        grid-column: 1;
      }
    }

    @media (max-width: 1000px) {
      &__main {
        background: transparent;
        padding: 0;
        box-shadow: none;
      }

      &__header {
        display: flex;
      }

      &__table ::v-deep .header {
        display: none;
      }

      &__table ::v-deep .body {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 20px;
      }

      &__row {
        &, &:nth-child(odd) {
          background: #FFFFFF;
          border-radius: 8px;
          padding: 12px 18px 22px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
      }

      ::v-deep .cell, &__cell {
        width: auto !important;
      }

      &__cell {
        display: flex;
        flex-direction: column-reverse;
        justify-self: flex-start;
        align-items: flex-start;
        gap: 5px;

        &:first-child {
          grid-column: 1 / 2 span;
          padding-bottom: 20px;
          border-bottom: 1px solid #EBEFF2;
          justify-self: stretch;
          gap: 10px;
        }
      }
    }

    @media (max-width: 576px) {
      &__tabs {
        overflow-x: scroll;
      }

      &__table ::v-deep .body {
        grid-template-columns: 1fr;
      }
    }
  }
</style>
